import { EquipmentType } from './data-model/equipment';

// The model registry now lives in the shared scan-models.ts (one registry
// for all scanners). See design-docs/scanner-model-config.md.

// ── Tunable constants ───────────────────────────────────────────────

/** Detected items at or above this confidence default to checked on the review screen.
 *  Items strictly below it default to unchecked and surface a warning icon + tooltip. */
export const EQUIPMENT_SCAN_CONFIDENCE_THRESHOLD = 0.7;

/** Two weights are considered identical for Merge dedupe if their absolute difference (in kg) is below this tolerance. */
export const EQUIPMENT_MERGE_TOLERANCE_KG = 0.05;

/** Maximum images per scan request. */
export const MAX_EQUIPMENT_SCAN_IMAGES = 4;

// ── Detected items (discriminated union by equipment type) ──────────
//
// Bar and Collar items are emitted at most once per result (a bar config
// has a single bar weight + a single per-collar weight). If the model
// produces multiple, the highest-confidence entry wins; the client logs
// and discards the rest. Plates / Stack / Fixed are repeating lists.

interface EquipmentScanItemBase {
  /** weight value in `ScanEquipmentResults.detectedUnit` */
  weight: number;
  /** 0..1; values strictly below `EQUIPMENT_SCAN_CONFIDENCE_THRESHOLD` are flagged as low-confidence */
  confidence: number;
}

export type EquipmentScanItem =
  | (EquipmentScanItemBase & { kind: 'stack' })
  | (EquipmentScanItemBase & { kind: 'plate' })
  | (EquipmentScanItemBase & { kind: 'bar' })
  | (EquipmentScanItemBase & { kind: 'collar' })
  | (EquipmentScanItemBase & { kind: 'fixed' });

// ── Status / errors ─────────────────────────────────────────────────

export enum ScanEquipmentStatus {
  Success = 'success',
  Blurry = 'blurry',
  NoEquipment = 'no_equipment',
  MixedEquipment = 'mixed_equipment',
  TypeMismatch = 'type_mismatch',
}

/** Reasons the AI returned no usable items (subset of ScanEquipmentStatus that excludes Success).
 *  TypeMismatch isn't strictly "no equipment" but is conveyed via `detectedType !== requestedType`
 *  in the result, not via this field. */
export type NoEquipmentReason = ScanEquipmentStatus.Blurry | ScanEquipmentStatus.NoEquipment | ScanEquipmentStatus.MixedEquipment;

export enum ScanEquipmentError {
  AuthRequired = 'auth_required',
  NoImage = 'no_image',
  TooManyImages = 'too_many_images',
  ImageTooLarge = 'image_too_large',
  UnsupportedEquipmentType = 'unsupported_equipment_type', // server rejects Manual / Unknown
  AllModelsRestricted = 'all_models_restricted',
  UnknownModel = 'unknown_model',
  ProviderError = 'provider_error',
  ProviderTimeout = 'provider_timeout',
  // the model stopped before returning a complete response — typically the output-token
  // budget was exhausted (a thinking model spent it reasoning). Transient: retrying the
  // same images usually succeeds, so the client keeps the captured photos.
  IncompleteResponse = 'incomplete_response',
}

// ── Quota ───────────────────────────────────────────────────────────

export enum ScanEquipmentQuotaRejection {
  AccountLocked = 'account_locked',
  RateLimited = 'rate_limited',
  DailyCooldown = 'daily_cooldown',
  QualityWarning = 'quality_warning',
  // the whole app's daily AI-scan ceiling was reached, not this user's own quota (#4032)
  GlobalCap = 'global_cap',
}

export interface ScanEquipmentQuota {
  scansToday: number;
  rejection?: ScanEquipmentQuotaRejection;
  cooldownUntil_utc?: string;
  lockedUntil_utc?: string;
}

// ── Client → Server params ──────────────────────────────────────────

/** Equipment types the scanner supports. Manual + Unknown are rejected server-side. */
export type ScanSupportedEquipmentType = Exclude<EquipmentType, EquipmentType.Manual | EquipmentType.Unknown>;

export interface ScanEquipmentParams {
  imagesBase64: string[];
  /** Hint to focus extraction on a specific type. If omitted, the AI auto-detects the type. */
  equipmentType?: ScanSupportedEquipmentType;
  model?: string;             // a model key from SCAN_MODELS, or 'all'
  // The user's active UI language (#4206). This scanner's AI output is not
  // localized today: `suggestedName` is branding read off the machine and is
  // copied as printed, and everything else it returns is numbers. The field is
  // carried so a future framing hook can read `params.locale` without another
  // client release.
  locale?: string;
}

// ── Server → Client results ─────────────────────────────────────────

/** Per-model payload returned when `params.model === 'all'` (multi-model dispatch). */
export interface ScanEquipmentModelResult {
  detectedType?: EquipmentType | 'unknown';
  suggestedName?: string;
  detectedUnit?: 'kg' | 'lb';
  confidence?: number;
  items?: EquipmentScanItem[];
  noEquipmentReason?: NoEquipmentReason;
  // the complete raw text the model returned, before our JSON parsing — kept so the
  // scan log can be diagnosed post-hoc. Carried on this result so it reaches the logger.
  rawResponse?: string;
  durationMs?: number;
  inputTokens?: number;
  outputTokens?: number;
  error?: string;
}

export interface ScanEquipmentResults {
  // single-model response
  detectedType?: EquipmentType | 'unknown';
  suggestedName?: string;
  detectedUnit?: 'kg' | 'lb';
  confidence?: number;
  items?: EquipmentScanItem[];
  noEquipmentReason?: NoEquipmentReason;
  modelKey?: string;

  // multi-model response (model='all')
  modelResults?: { [modelKey: string]: ScanEquipmentModelResult };
  scanId?: string;

  // quota piggybacks on every scan response
  quota?: ScanEquipmentQuota;

  error?: ScanEquipmentError | string;
}
